import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, RotateCcw, PhoneCall, Stethoscope, CalendarDays, Crown, FileText } from "lucide-react";

const SECTIONS = [
  ["customer_returns", "Customers due to return", RotateCcw],
  ["followups", "Follow-ups due", PhoneCall],
  ["vet_followups", "Veterinary follow-ups", Stethoscope],
  ["upcoming_bookings", "Upcoming bookings", CalendarDays],
  ["unassigned_bookings", "Unassigned bookings", CalendarDays],
  ["memberships", "Membership actions", Crown],
  ["outstanding_invoices", "Outstanding invoices", FileText],
];
const BADGE = { OVERDUE: "bg-rose-100 text-rose-700", overdue: "bg-rose-100 text-rose-700", expired: "bg-rose-100 text-rose-700", DUE: "bg-amber-100 text-amber-700", due_today: "bg-amber-100 text-amber-700", expiring: "bg-amber-100 text-amber-700" };

function wa(m) { const d = (m || "").replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}`; }

export default function ActionCenter() {
  const nav = useNavigate();
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ["action-center"], queryFn: async () => (await api.get("/action-center")).data });
  if (isLoading || !data) return <Loading label="Loading action center" />;
  const sections = data.sections || {};

  const go = (action, it) => {
    if (action === "open_customer") nav(`/customers/${it.customer_id}`);
    else if (action === "open_booking") nav(`/bookings`);
    else if (action === "open_followup") nav(`/follow-ups`);
    else if (action === "open_vet_followup") nav(`/follow-ups`);
    else if (action === "open_invoice") nav(`/invoices`);
    else if (action === "book") nav(`/bookings`);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <PageHeader title="Action Center" subtitle="What needs attention today — from recorded data. Descriptive, not predictive." />
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="ac-refresh" className="gap-1"><RotateCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />Refresh</Button>
      </div>
      <div className="space-y-5">
        {SECTIONS.filter(([k]) => sections[k]).map(([key, label, Icon]) => {
          const sec = sections[key];
          return (
            <div key={key} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`ac-section-${key}`}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                <Icon className="h-4 w-4 text-slate-500" />
                <h3 className="font-semibold text-slate-800">{label}</h3>
                <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600" data-testid={`ac-count-${key}`}>{sec.count}</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-80 overflow-auto">
                {sec.items.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nothing pending.</p>}
                {sec.items.map((it, i) => (
                  <div key={it.id || it.customer_id || i} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm" data-testid={`ac-item-${key}-${i}`}>
                    <div className="min-w-[130px] flex-1">
                      <div className="font-medium text-slate-900">{it.title || "—"}{it.amount != null && <span className="ml-2 text-slate-500">{inr(it.amount)}</span>}</div>
                      {it.reason && <div className="text-xs text-slate-400">{it.reason}</div>}
                    </div>
                    {it.status && <span className={`rounded-full px-2 py-0.5 text-xs ${BADGE[it.status] || "bg-slate-100 text-slate-600"}`}>{String(it.status).replace(/_/g, " ")}</span>}
                    {it.date && <span className="text-xs text-slate-400 min-w-[90px]">{String(it.date).slice(0, 10)}</span>}
                    <div className="flex gap-1.5">
                      {it.actions?.includes("call") && it.mobile && <a href={`tel:${it.mobile}`} data-testid={`ac-call-${i}`}><Button size="sm" variant="outline" className="gap-1"><Phone className="h-3.5 w-3.5" /></Button></a>}
                      {it.actions?.includes("whatsapp") && it.mobile && <a href={wa(it.mobile)} target="_blank" rel="noreferrer" data-testid={`ac-wa-${i}`}><Button size="sm" variant="outline" className="gap-1"><MessageCircle className="h-3.5 w-3.5" /></Button></a>}
                      {it.actions?.filter((a) => a.startsWith("open") || a === "book").map((a) => (
                        <Button key={a} size="sm" onClick={() => go(a, it)} data-testid={`ac-${a}-${i}`} className="bg-slate-900 text-white text-xs">{a.replace("open_", "").replace(/_/g, " ")}</Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
